import React, { useEffect, useState, useMemo, useCallback } from "react"
import { Offcanvas, Row, Col, Form, InputGroup, Button, Badge, Spinner } from "react-bootstrap"
import { useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { Routes } from "../../routes"
import Settings from "../../common/Settings"
import moment from "moment"
import Pagination from "../core/Pagination"
import { confirmAlert } from "react-confirm-alert"

interface VisitHistorySidebarProps {
    show: boolean
    toggle: () => void
    placement?: "start" | "end"
    allowMapInteraction?: boolean
}

interface HistoryRecord {
    placeId: string
    placeName: string
    note: string
    image: string
    author: {
        firstName: string
        lastName: string
    }
    created: string
}

const VisitHistorySidebar: React.FC<VisitHistorySidebarProps> = ({
    show,
    toggle,
    placement = "end",
    allowMapInteraction = true
}) => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    // Estados principales
    const [records, setRecords] = useState<HistoryRecord[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>("")
    const [isPinned, setIsPinned] = useState<boolean>(true) // Pinned por defecto para interacción con mapa

    // Estados de paginación
    const [page, setPage] = useState<number>(0)
    const [quantityOfPages, setQuantityOfPages] = useState<number>(1)
    const [totalRecords, setTotalRecords] = useState<number>(0)
    const quantity = 15 // Reducido para carga más rápida

    // Estados de filtros
    const [searchTerm, setSearchTerm] = useState<string>("")
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")

    // Debounce para el término de búsqueda
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchTerm])

    // Función memoizada para obtener historial
    const requestHistories = useCallback(async (resetPage = false) => {
        if (!user?.OrganizationId) return

        setLoading(true)
        setError("")

        try {
            const currentPage = resetPage ? 0 : page
            let query = new URLSearchParams({
                page: currentPage.toString(),
                quantity: quantity.toString(),
                organizationId: user.OrganizationId,
                ...(debouncedSearchTerm && { name: debouncedSearchTerm })
            }).toString()

            const response = await API.getAction("place/history", query)

            if (response.data.status === "ok") {
                setRecords(response.data.response || [])
                setQuantityOfPages(response.data.quantityOfPages || 1)
                setTotalRecords(response.data.quantityOfRecords || 0)

                if (resetPage) {
                    setPage(0)
                }
            } else {
                setError(t("Error loading history"))
            }
        } catch (err) {
            console.error("Error fetching history:", err)
            setError(t("Error loading history"))
        } finally {
            setLoading(false)
        }
    }, [page, quantity, user?.OrganizationId, debouncedSearchTerm, t])

    // Efecto para cargar datos cuando cambia el término de búsqueda
    useEffect(() => {
        if (show) {
            requestHistories(true) // Reset page cuando hay nueva búsqueda
        }
    }, [debouncedSearchTerm, show])

    // Efecto para cargar datos cuando cambia la página
    useEffect(() => {
        if (show && page > 0) {
            requestHistories(false)
        }
    }, [page, show])

    // Efecto inicial para cargar datos
    useEffect(() => {
        if (show) {
            requestHistories(true)
        }
    }, [show])

    // Función para navegar a lugar específico
    const handlePlaceClick = useCallback((placeId: string) => {
        history.push(Routes.PlaceDetails.clean + placeId)
        // Solo cerrar si no está pinned o si el usuario lo desea
        if (!isPinned) {
            toggle()
        }
    }, [history, toggle, isPinned])

    // Función para toggle pin state
    const handleTogglePin = useCallback(() => {
        setIsPinned(!isPinned)
    }, [isPinned])

    // Función para limpiar búsqueda
    const handleClearSearch = useCallback(() => {
        setSearchTerm("")
    }, [])

    const deleteRecord = async (item: any) => {
        if (item) {
            confirmAlert({
                closeOnClickOutside: false,
                message: t("Are you sure you want to delete this record?") ?? "",
                buttons: [
                    {
                        label: t("Yes"),
                        onClick: async () => {
                            await API.getAction("placeVisit/Delete" + "?id=" + item.id)
                            await requestHistories(false);

                        },
                    },
                    {
                        label: "No",
                        onClick: () => {
                            console.log("no")
                        },
                    },
                ],
            })
        }
    }

    // Recordes memoizados para evitar re-renders innecesarios
    const recordsList = useMemo(() => {
        if (!records || records.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="text-muted">
                        <i className="fa fa-history fa-3x mb-3 opacity-50"></i>
                        <h6 className="mb-2">{t("No history available")}</h6>
                        <small>{debouncedSearchTerm ? t("No results found for your search") : t("No history records found")}</small>
                    </div>
                </div>
            )
        }

        return records.map((item: HistoryRecord, index: number) => (
            <div
                key={`${item.placeId}-${index}`}
                className="border-bottom pb-3 mb-3 cursor-pointer"

                style={{
                    transition: "background-color 0.2s ease",
                    cursor: "pointer",
                    borderRadius: "8px",
                    padding: "8px"
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa"
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent"
                }}
            >
                <Row className="g-2 align-items-center">
                    <Col xs={4}>
                        <div className="position-relative">
                            <img
                                src={`${Settings.BasePathForAPI}file/getThumbnail?filename=${item.image}`}
                                loading="lazy"
                                alt={item.placeName}
                                className="img-fluid rounded"
                                style={{
                                    height: "60px",
                                    width: "100%",
                                    objectFit: "cover"
                                }}
                                onError={(e) => {
                                    e.currentTarget.src = "/placeholder-image.png"
                                }}
                            />
                        </div>
                    </Col>
                    <Col xs={8}>
                        <div>
                            <h6 className="mb-1 text-truncate fw-bold"
                                style={{ fontSize: "0.9rem" }}
                                onClick={() => handlePlaceClick(item.placeId)}
                            >
                                {item.placeName}
                            </h6>
                            {item.note && (
                                <p className="mb-1 text-muted small text-truncate" style={{ fontSize: "0.8rem" }}>
                                    {item.note}
                                </p>
                            )}
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-primary fw-medium">
                                    {`${item.author.firstName ?? ""} ${item.author.lastName ?? ""}`.trim()}
                                </small>
                                <Badge bg="light" text="muted" className="small">
                                    {moment(item.created).fromNow()}
                                </Badge>
                                <button className="btn btn-sm btn-danger" onClick={() => deleteRecord(item)}>
                                    <i className="fa fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        ))
    }, [records, debouncedSearchTerm, handlePlaceClick, t])

    return (
        <Offcanvas
            show={show}
            onHide={toggle}
            placement={placement}
            backdrop={false} // Permitir interacción con el mapa
            scroll={true}    // Permitir scroll en el fondo
            style={{ width: "380px", zIndex: 1040 }} // Z-index menor para no bloquear mapa
            className={`visit-history-sidebar `}
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title className="d-flex align-items-center">
                    <i className="fa fa-history me-2 text-primary"></i>
                    {t("Visit History")}
                    {totalRecords > 0 && (
                        <Badge bg="primary" className="ms-2 small">
                            {totalRecords}
                        </Badge>
                    )}
                </Offcanvas.Title>

                {/* Pin/Unpin button para mantener abierto mientras se interactúa con mapa */}
                <div className="d-flex align-items-center">
                    <Button
                        variant="outline-light"
                        size="sm"
                        onClick={handleTogglePin}
                        className="me-2"
                        title={isPinned ? t("Unpin - Close when clicking places") : t("Pin - Keep open when clicking places")}
                    >
                        <i className={`fa ${isPinned ? 'fa-thumbtack' : 'fa-map-pin'}`}></i>
                    </Button>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-0">
                {/* Search Section */}
                <div className="p-3 bg-light border-bottom">
                    <InputGroup size="sm">
                        <Form.Control
                            type="text"
                            placeholder={t("Search places...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                        {searchTerm && (
                            <Button
                                variant="outline-secondary"
                                onClick={handleClearSearch}
                                disabled={loading}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        )}
                        {loading && (
                            <Button variant="outline-secondary" disabled>
                                <Spinner as="span" animation="border" size="sm" />
                            </Button>
                        )}
                    </InputGroup>
                </div>

                {/* Content Section */}
                <div className="flex-grow-1 p-3" style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
                    {error ? (
                        <div className="alert alert-danger" role="alert">
                            <i className="fa fa-exclamation-triangle me-2"></i>
                            {error}
                            <Button
                                size="sm"
                                variant="outline-danger"
                                className="ms-2"
                                onClick={() => requestHistories(true)}
                            >
                                {t("Retry")}
                            </Button>
                        </div>
                    ) : (
                        recordsList
                    )}
                </div>

                {/* Pagination Section */}
                {quantityOfPages > 1 && !loading && (
                    <div className="border-top p-3 bg-white">
                        <Pagination
                            currentPage={page}
                            totalPages={quantityOfPages}
                            onPageChanged={setPage}
                        />
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default VisitHistorySidebar
